import React, { useState, createContext, useContext, useMemo } from 'react';
import OtpInput from 'react-otp-input';
import mainlogo from "assets/img/favicon.png";
import { PasswordInput, TextInput } from 'common/Forms/inputs';
import { Form, FormField as BaseFormField, PrimaryButton } from 'common/Forms';
import { useLoginMutation, useForgotPasswordMutation, useVerifyUserMutation } from 'store/action';
import { useNavigate } from 'react-router-dom';
import * as paths from 'app-router/paths';
import { toast } from 'react-toastify';
import { captureComponentError } from 'utils/sentry';
import { usePermissions } from 'context/PermissionsContext';
import { ModalDialog } from 'common/Dialog';

const FormErrorContext = createContext({});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?!.*\s).{8,}$/;

const FormField = (props) => {
  const errors = useContext(FormErrorContext);
  const { inputFactory, ...rest } = props;
  return (
    <BaseFormField
      {...rest}
      errorMessage={errors[props.id]}
      inputFactory={inputFactory}
    />
  );
};

const LoginPage = () => {
  const [loginApi, { isLoading }] = useLoginMutation();
  const [forgotPasswordApi, { isLoading: isForgotPasswordLoading }] = useForgotPasswordMutation();
  const [verifyUserApi, { isLoading: isVerifyUserLoading }] = useVerifyUserMutation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const { refreshPermissions } = usePermissions();

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showLoginOTP, setShowLoginOTP] = useState(false);

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userId, setUserId] = useState(null);
  const [dirtyFields, setDirtyFields] = useState({});

  const navigateBack = () => navigate(paths.buildDashboardPath());

  const markDirty = (id) => setDirtyFields(prev => ({ ...prev, [id]: true }));

  // ── Login ────────────────────────────────────────────────────────────────────

  const handleLogin = async () => {
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    if (!trimmedEmail || !trimmedPassword) {
      toast.warn('Email or Password is missing.');
      return;
    }
    if (!emailRegex.test(trimmedEmail)) {
      toast.error('Invalid email format.');
      return;
    }

    try {
      const response = await loginApi({ email: trimmedEmail, password: trimmedPassword }).unwrap();
      if (response?.status === 'SUCCESS' || response?.message?.toLowerCase().includes('otp') || response?.data?.userId || response?.userId) {
        setUserId(response?.data?.userId || response?.userId);
        setOtp('');
        setShowLoginOTP(true);
        toast.info('Please enter the OTP sent to your email.');
      } else {
        toast.error('Your entered details are invalid.');
      }
    } catch (err) {
      captureComponentError(err, { component: 'LoginPage', action: 'handleLogin' });
      toast.error(err?.data?.message || 'Login failed, please try again.');
    }
  };

  const handleLoginOTPSubmit = async () => {
    const trimmedOtp = otp.trim();
    if (!/^\d{4}$/.test(trimmedOtp)) {
      toast.warn('Please enter a valid 4-digit OTP.');
      return;
    }
    try {
      const response = await verifyUserApi({ userId, otp: trimmedOtp }).unwrap();
      const token = response?.data?.token || response?.token;
      if (token) {
        localStorage.setItem('token', token);
        refreshPermissions();
        navigateBack();
        toast.success('Logged in successfully');
        setShowLoginOTP(false);
        setOtp('');
      } else {
        toast.error('Invalid OTP.');
      }
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to verify OTP.');
    }
  };

  // ── Forgot Password ──────────────────────────────────────────────────────────

  const handleForgotPasswordSubmit = async () => {
    markDirty('forgot-password-email');
    const trimmedEmail = forgotPasswordEmail.trim();
    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) return;

    try {
      const response = await forgotPasswordApi({ email: trimmedEmail }).unwrap();
      setUserId(response?.data?.userId || response?.userId);
      toast.success(response?.message || 'OTP sent successfully');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setDirtyFields({});
      setShowForgotPassword(false);
      setShowResetPassword(true);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to process request.');
    }
  };

  const handleResendOTP = async () => {
    const trimmedEmail = forgotPasswordEmail.trim();
    try {
      const response = await forgotPasswordApi({ email: trimmedEmail }).unwrap();
      setUserId(response?.data?.userId || response?.userId);
      toast.success(response?.message || 'OTP resent successfully');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to resend OTP.');
    }
  };

  // ── Reset Password ───────────────────────────────────────────────────────────

  const passwordRules = [
    { label: 'At least 8 characters', valid: newPassword.length >= 8 },
    { label: 'One uppercase letter', valid: /[A-Z]/.test(newPassword) },
    { label: 'One lowercase letter', valid: /[a-z]/.test(newPassword) },
    { label: 'One number', valid: /[0-9]/.test(newPassword) },
    { label: 'One special character', valid: /[^A-Za-z0-9]/.test(newPassword) },
    { label: 'No spaces', valid: newPassword.length > 0 && !/\s/.test(newPassword) },
  ];

  const passwordError = dirtyFields['reset-password-new'] && !newPassword ? 'New password is required.' : null;
  const confirmError = confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match.' : null;

  const isPasswordValid = passwordRegex.test(newPassword);
  const isResetButtonDisabled = !/^\d{4}$/.test(otp) || !isPasswordValid || newPassword !== confirmPassword || !confirmPassword;

  const handleResetPasswordSubmit = async () => {
    markDirty('reset-password-new');
    markDirty('reset-password-confirm');

    if (!/^\d{4}$/.test(otp)) {
      toast.warn('Please enter a valid 4-digit OTP.');
      return;
    }
    if (!isPasswordValid) {
      toast.warn('Please enter a valid password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warn('Passwords do not match.');
      return;
    }

    try {
      const response = await verifyUserApi({
        userId,
        otp: otp.trim(),
        newPassword,
      }).unwrap();

      const token = response?.data?.token || response?.token;
      if (token) {
        localStorage.setItem('token', token);
        refreshPermissions();
        navigateBack();
        toast.success('Password reset successfully');
      } else {
        toast.success('Password reset successfully! Please login.');
      }

      setShowResetPassword(false);
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setUserId(null);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to reset password.');
    }
  };

  // ── Error maps ───────────────────────────────────────────────────────────────

  const forgotPasswordErrors = useMemo(() => {
    if (!dirtyFields['forgot-password-email']) return {};
    const trimmedEmail = forgotPasswordEmail.trim();
    if (!trimmedEmail) return { 'forgot-password-email': 'Email address is required.' };
    if (!emailRegex.test(trimmedEmail)) return { 'forgot-password-email': 'Invalid email format.' };
    return {};
  }, [forgotPasswordEmail, dirtyFields]);

  const resetPasswordErrors = useMemo(() => {
    const errors = {};
    if (passwordError) errors['reset-password-new'] = passwordError;
    if (confirmError) errors['reset-password-confirm'] = confirmError;
    return errors;
  }, [passwordError, confirmError]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <React.Fragment>
      <div className="relative flex justify-center items-center h-screen w-screen capitalize">
        <div className="absolute bg-white p-6 border-2 rounded-md shadow-lg w-full max-w-md mx-4 sm:mx-8 border-t-2">
          <div className="flex flex-col justify-center items-center gap-2 mb-6">
            <img src={mainlogo} alt="Isolation Mode" className="w-[80px] h-[80px]" />
            <span className="text-2xl sm:text-3xl font-semibold">
              Welcome Back!
            </span>
            <span className="text-sm sm:text-base text-gray-600">
              Please enter your details
            </span>
          </div>

          <Form onEnter={handleLogin}>
            <FormField
              id="email"
              name="email"
              label="Email Address"
              inputFactory={(id, name, onValidation) => (
                <TextInput
                  id={id}
                  name={name}
                  type="email"
                  placeholder="Enter email"
                  onValidation={onValidation}
                  value={email}
                  onChange={(_, value) => setEmail(value)}
                />
              )}
            />
            <FormField
              id="password"
              name="password"
              label="Password"
              inputFactory={(id, name, onValidation) => (
                <PasswordInput
                  id={id}
                  name={name}
                  placeholder="Enter password"
                  onValidation={onValidation}
                  value={password}
                  onChange={(_, value) => setPassword(value)}
                />
              )}
            />

            <div className="flex justify-end mb-2">
              <button
                type="button"
                className="text-sm font-medium text-secondary-primary_button_bg hover:underline cursor-pointer"
                onClick={() => {
                  setForgotPasswordEmail('');
                  setDirtyFields({});
                  setShowForgotPassword(true);
                }}
              >
                Forgot password?
              </button>
            </div>

            <div className="flex justify-center items-center mt-4">
              <PrimaryButton disabled={isLoading} action={handleLogin}>
                Login
              </PrimaryButton>
            </div>
          </Form>
        </div>

        {/* Forgot Password Modal */}
        <FormErrorContext.Provider value={forgotPasswordErrors}>
          <ModalDialog
            isOpened={showForgotPassword}
            onClose={() => setShowForgotPassword(false)}
            title="Forgot Password"
            classNames={['max-w-md w-full']}
          >
            <div className="flex flex-col gap-4">
              <FormField
                id="forgot-password-email"
                name="email"
                label="Email"
                required={true}
                inputFactory={(id, name) => (
                  <TextInput
                    id={id}
                    name={name}
                    type="email"
                    placeholder="Enter your email"
                    value={forgotPasswordEmail}
                    onChange={(_, value) => setForgotPasswordEmail(value)}
                    onEnter={() => markDirty(id)}
                  />
                )}
              />
              <div className="flex justify-center mt-2">
                <PrimaryButton action={handleForgotPasswordSubmit} isWide={true} disabled={isForgotPasswordLoading}>
                  {isForgotPasswordLoading ? 'Submitting...' : 'Submit'}
                </PrimaryButton>
              </div>
            </div>
          </ModalDialog>
        </FormErrorContext.Provider>

        {/* Reset Password Modal (OTP + new password + confirm) */}
        <FormErrorContext.Provider value={resetPasswordErrors}>
          <ModalDialog
            isOpened={showResetPassword}
            onClose={() => {
              setShowResetPassword(false);
              setOtp('');
              setNewPassword('');
              setConfirmPassword('');
              setDirtyFields({});
            }}
            title="Reset Password"
            classNames={['max-w-md w-full']}
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">OTP <span className="text-red-500">*</span></label>
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={4}
                  inputType="number"
                  shouldAutoFocus={true}
                  renderInput={(props) => <input {...props} />}
                  renderSeparator={<span className="w-2" />}
                  containerStyle={{ justifyContent: 'center' }}
                  inputStyle="!w-12 !h-12 text-xl text-center border border-secondary rounded-lg outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <div className="flex justify-end -mt-2">
                <button
                  type="button"
                  disabled={isForgotPasswordLoading}
                  className="text-sm text-secondary-primary_button_bg hover:underline cursor-pointer disabled:opacity-50"
                  onClick={handleResendOTP}
                >
                  Resend OTP
                </button>
              </div>

              <FormField
                id="reset-password-new"
                name="newPassword"
                label="New Password"
                required={true}
                inputFactory={(id, name) => (
                  <PasswordInput
                    id={id}
                    name={name}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(_, value) => setNewPassword(value)}
                    onEnter={() => markDirty(id)}
                  />
                )}
              />

              {(dirtyFields['reset-password-new'] || newPassword) && (
                <ul className="text-xs flex flex-col gap-1 -mt-2 pl-1">
                  {passwordRules.map((rule) => (
                    <li key={rule.label} className={`flex items-center gap-1.5 ${rule.valid ? 'text-green-600' : 'text-red-500'}`}>
                      <span>{rule.valid ? '✓' : '✗'}</span>
                      {rule.label}
                    </li>
                  ))}
                </ul>
              )}

              <FormField
                id="reset-password-confirm"
                name="confirmPassword"
                label="Confirm Password"
                required={true}
                inputFactory={(id, name) => (
                  <PasswordInput
                    id={id}
                    name={name}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(_, value) => setConfirmPassword(value)}
                    onEnter={() => markDirty(id)}
                  />
                )}
              />

              <div className="flex justify-center mt-4">
                <PrimaryButton
                  action={handleResetPasswordSubmit}
                  isWide={true}
                  disabled={isResetButtonDisabled || isVerifyUserLoading}
                >
                  {isVerifyUserLoading ? 'Resetting...' : 'Reset Password'}
                </PrimaryButton>
              </div>
            </div>
          </ModalDialog>
        </FormErrorContext.Provider>

        {/* Login OTP Modal */}
        <ModalDialog
          isOpened={showLoginOTP}
          onClose={() => {
            setShowLoginOTP(false);
            setOtp('');
          }}
          title="OTP Verification"
          classNames={['max-w-md w-full']}
        >
          <div className="flex flex-col items-center gap-6 py-2">
            <div className="flex flex-col items-center gap-1">
              <p className="text-sm text-gray-500 text-center">
                Enter the 4-digit code sent to your email address.
              </p>
            </div>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={4}
              inputType="number"
              shouldAutoFocus={true}
              renderInput={(props) => (
                <input
                  {...props}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleLoginOTPSubmit();
                    props.onKeyDown?.(e);
                  }}
                />
              )}
              renderSeparator={<span className="w-3" />}
              containerStyle={{ justifyContent: 'center' }}
              inputStyle="!w-14 !h-14 text-xl text-center border-2 border-secondary rounded-xl outline-none transition-all shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <div className="flex flex-col items-center gap-3 w-full">
              <PrimaryButton action={handleLoginOTPSubmit} isWide={true} disabled={isVerifyUserLoading || !/^\d{4}$/.test(otp)}>
                {isVerifyUserLoading ? 'Verifying...' : 'Verify OTP'}
              </PrimaryButton>
              <button
                type="button"
                disabled={isLoading}
                className="text-sm text-secondary-primary_button_bg hover:underline cursor-pointer disabled:opacity-50"
                onClick={handleLogin}
              >
                Didn't receive the code? <span className="font-medium">Resend OTP</span>
              </button>
            </div>
          </div>
        </ModalDialog>
      </div>
    </React.Fragment>
  );
};

export default LoginPage;
